import { FormattedMessage, useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Deal, Plan } from '../../types/plans';

import {
  getCurrencySymbol,
  formatPrice,
  getMonthlyPriceData,
  useReducedPrice,
} from './utils';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

/** Monthly price component. */

/**
 * Displays the monthly or daily price for a given plan, including deal pricing if applicable.
 * @param props - The component props.
 * @param props.plan - The plan details.
 * @param props.monthlyDailyPrice - Indicates whether to show monthly or daily price.
 * @param props.deal - Optional deal details.
 */
export const MonthlyPrice = (props: {
  plan: Plan;
  monthlyDailyPrice: string | null;
  deal?: Deal;
}) => {
  const { plan, monthlyDailyPrice, deal } = props;
  const isMonthly = monthlyDailyPrice === 'monthly';

  const isDeal = deal && Object.keys(deal).length > 0;
  const currencySymbol = getCurrencySymbol(plan.currency.currency);

  const intl = useIntl();

  // The Matrix Selector only has the origUnitAmount to show the original price
  // PlanSelector only has the unitAmount to show the current price

  const dealAnnual = useReducedPrice(!!isDeal, plan, deal);
  const unitAmount = plan.currency.origUnitAmount || plan.currency.unitAmount;

  const annualPrice = Math.floor(unitAmount);
  const monthlyPrice = Math.floor(unitAmount / plan.intervalLength);
  const days = 30.44 * plan.intervalLength; // Average days in a month
  const dailyPrice = (unitAmount / days).toFixed(2);

  // Deals calculated from reduced price

  const dealMonthlyPrice = ((dealAnnual ?? 0) / plan.intervalLength).toFixed(0);

  const dealDailyPrice = ((dealAnnual ?? 0) / days).toFixed(2);

  const { text } = getMonthlyPriceData(plan, intl);

  return (
    <Flex className="flex flex-col items-center" data-testid="monthly-price">
      {isMonthly ? (
        <>
          {isDeal && (
            <Flex className="font-national-2 my-2 items-center justify-center text-sm line-through">
              <FormattedMessage
                id="dealMonthlyPrice"
                defaultMessage="{currencySymbol}{price}/month"
                values={{
                  currencySymbol,
                  price: monthlyPrice,
                }}
              />
            </Flex>
          )}
          <Flex
            className={cn('font-national-2 my-2 items-center text-xl', text5)}
          >
            <FormattedMessage
              id="monthlyPrice"
              defaultMessage="{currencySymbol}{price}/month"
              values={{
                currencySymbol: <span key="currencySymbol" className="">{currencySymbol}</span>,
                price: (
                  <span key="price" className="">{formatPrice(dealMonthlyPrice)}</span>
                ),
              }}
            />
          </Flex>
        </>
      ) : (
        <>
          {isDeal && (
            <Flex className="items-center justify-center text-xs line-through">
              <FormattedMessage
                id="dealMonthlyPrice"
                defaultMessage="{currencySymbol}{price}/day"
                values={{
                  currencySymbol: currencySymbol,
                  price: dailyPrice,
                }}
              />
            </Flex>
          )}
          <Flex className="items-center text-xl">
            <FormattedMessage
              id="dailyPrice"
              defaultMessage="{currencySymbol}{price}/day"
              values={{
                currencySymbol: (
                  <span key="currencySymbol" className="font-extrabold">{currencySymbol}</span>
                ),
                price: <span key="price" className="font-extrabold">{dealDailyPrice}</span>,
              }}
            />
          </Flex>
        </>
      )}
      <p className="text-wp-gray font-national-2 pb-6 text-center text-sm">
        <FormattedMessage
          id="annualPrice"
          defaultMessage="Total {currencySymbol}{price} billed {text}"
          values={{
            currencySymbol,
            price: isDeal ? dealAnnual : annualPrice,
            text: text,
          }}
        />
      </p>
    </Flex>
  );
};
